/* Copyright (c) 2020-present CloudIO Inc */

import { IoAudit, DBRow } from '@cloudio-saas/datasource-types';
import { atom, useStateXAtom } from '@cloudio-saas/statex';
import { FormLabel, Theme, List, ListItem, ListItemText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import VirtualList from 'components/DataGrid/grid/List';
import TimeAgo from 'react-timeago';
import Icon from 'icons/Icon';
import React, { CSSProperties } from 'react';
import { useAutoSizerContext } from 'hooks/useComponentSize';
import Store from 'store/Store';
import useAppStore, { useRecord } from 'store/useAppStore';
import NoDataFound from 'utils/NoDataFound';
import Checkbox from '../fields/Checkbox';

const useStyles = makeStyles((theme: Theme) => ({
  editIcon: {
    display: 'none',
  },
  listItem: {
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
  },
  check: {
    minWidth: 42,
    maxWidth: 42,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

export const selectionAtom = atom<[number | null, number | null]>({
  path: ['transient', 'compareFunction'],
  defaultValue: [null, null],
});

function AppCell({
  value,
  style,
  store,
}: {
  value: string;
  style?: CSSProperties;
  store: Store<IoAudit>;
}) {
  const classes = useStyles();
  const { record } = useRecord(store, value);
  const [selection, setSelection] = useStateXAtom(selectionAtom);

  const rec = record as DBRow<IoAudit>;
  if (!record) return null;
  return (
    <ListItem className={classes.listItem} dense key={value} style={style}>
      <ListItemText
        primary={
          <>
            <TimeAgo date={rec.updateDate} /> by {rec.updatedBy}
          </>
        }
      />
      <Checkbox
        data-testid={`${rec.auditId}`}
        checked={selection[0] === rec.auditId || selection[1] === rec.auditId}
        className={classes.check}
        onChange={(e) => {
          const sel: [number | null, number | null] = [...selection];
          if (!e.target.checked) {
            if (sel[0] === rec.auditId) {
              sel[0] = null;
            } else if (sel[1] === rec.auditId) {
              sel[1] = null;
            }
          } else if (sel[1]) {
            sel[0] = sel[1];
            sel[1] = rec.auditId;
          } else if (sel[0]) {
            sel[1] = rec.auditId;
          } else {
            sel[0] = rec.auditId;
          }
          setSelection(sel);
        }}
      />
    </ListItem>
  );
}

const PageControllerHistory = ({
  appUid,
}: {
  appUid: string;
}): React.ReactElement => {
  const { store, recordsOrder } = useAppStore<IoAudit>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoAudit',
    alias: 'IoAudit-alias',
  });
  store.readData();
  const classes = useStyles();
  const { width, height } = useAutoSizerContext();

  if (recordsOrder.length === 0) {
    return (
      <NoDataFound message="You will see the history once you start making changes to this controller." />
    );
  }

  return (
    <List>
      <ListItem className={classes.header} dense style={{ width, height: 40 }}>
        <ListItemText primary="Updated" />
        <FormLabel
          data-tip="Select any two auditId to view the diff"
          className={classes.check}
        >
          <Icon iconName="eye" />
        </FormLabel>
      </ListItem>
      <VirtualList
        CellComponent={({ rowIndex, style }) => (
          <AppCell style={style} value={recordsOrder[rowIndex]} store={store} />
        )}
        rowHeight={40}
        height={height - 40 - 16}
        totalRows={recordsOrder.length}
        width={width}
        custom
      />
    </List>
  );
};

export default PageControllerHistory;
